import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Inbox, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { orpc } from "@/client";
import { InboxCard } from "@/components/realtime/inbox-card";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { MessagingStatus } from "@/components/realtime/messaging-status";
import { AgoraLabels } from "@/lib/labels";

function LoadingCard() {
	return (
		<div className="border-t border-border py-3 first:border-t-0">
			<div className="flex items-baseline justify-between gap-2.5">
				<Skeleton className="h-3.5 w-32" />
				<Skeleton className="h-3 w-10" />
			</div>

			<div className="mt-2 flex flex-col gap-1.5">
				<Skeleton className="h-3 w-full" />
				<Skeleton className="h-3 w-3/4" />
			</div>
		</div>
	);
}

export function AgoraSidePanel() {
	const { data, isLoading, isError, isFetching, refetch } = useQuery(
		orpc.realtime.inbox.queryOptions({
			staleTime: Infinity,
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		}),
	);

	const cards = data?.cards || [];

	useEffect(() => {
		if (!data?.expires_at) {
			return;
		}
		const timer = setTimeout(
			() => {
				void refetch();
			},
			Math.max(0, data.expires_at.getTime() - Date.now()) + 50,
		);
		return () => clearTimeout(timer);
	}, [data?.expires_at, refetch]);

	return (
		<section
			data-component="agora-side-panel"
			aria-label={AgoraLabels.panelTitle}
			className="flex max-h-[calc(100dvh-10rem)] min-w-0 flex-col"
		>
			<div className="workspace-section-heading shrink-0">
				<h2 className="text-base font-semibold text-foreground">{AgoraLabels.panelTitle}</h2>

				{!!data && <MessagingStatus />}
			</div>

			<div className="min-h-0 overflow-y-auto overscroll-contain">
				{isError && (
					<div role="alert" className="flex flex-col items-start gap-3 p-4">
						<p className="text-sm text-destructive">{AgoraLabels.panelError}</p>
						<Button
							type="button"
							size="sm"
							variant="outline"
							disabled={isFetching}
							onClick={() => refetch()}
						>
							<RefreshCw />
							Tentar de novo
						</Button>
					</div>
				)}

				{!isError && isLoading && (
					<div className="flex flex-col px-4">
						{Array.from({ length: 4 }, (_, i) => (
							<LoadingCard key={i} />
						))}
					</div>
				)}

				{!isError && !isLoading && cards.length === 0 && (
					<EmptyState icon={Inbox} title={AgoraLabels.panelEmpty} />
				)}

				{cards.length > 0 && (
					<div className="flex flex-col px-4">
						{cards.map((card) => (
							<InboxCard key={card.person_id} card={card} />
						))}
					</div>
				)}
			</div>
		</section>
	);
}
